/**
 * Incubator / Accelerator Cockpit — management-style dashboard.
 *
 * Sections:
 *   1. Hero — program greeting + 4 hero KPIs (Active Startups, Total
 *      Funding Raised, Avg Health Score, Active Mentors).
 *   2. Cohort progress — current cohort name + week N/12 + on-track %.
 *   3. Iqra AI Insights — 3 callouts (at-risk startups, mentor-cap warning,
 *      hot intros).
 *   4. Startup health distribution — green/amber/red split bar.
 *   5. Top 5 portfolio startups by health.
 *   6. Action items + Mentor utilization heatmap.
 *
 * Data: GET /startups/, /startups/agreements. Falls back to demo data
 * for the demo tenant (purged on GO LIVE).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Rocket, Users, GraduationCap, Sparkles, ChevronRight,
  Target, Award, Activity, AlertTriangle, Flame, Clock,
  ArrowUpRight, ArrowDownRight, CheckCircle2, Heart,
  TrendingUp,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

function fmtMoney(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function Sparkline({ data, color = '#8B3FC5', height = 26, width = 88 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = Math.max(1, max - min);
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * step},${height - ((v - min) / range) * height}`).join(' ');
  const lastY = height - ((data[data.length - 1] - min) / range) * height;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={pts}/>
      <circle cx={(data.length - 1) * step} cy={lastY} r="2.5" fill={color}/>
    </svg>
  );
}

function KpiTile({ label, value, delta, sparkData, icon: Icon, accent, testid }) {
  const positive = (delta ?? 0) >= 0;
  return (
    <div
      className="ve-card p-5 group transition-all hover:border-[var(--ve-violet)] hover:shadow-lg relative overflow-hidden"
      data-testid={testid}
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ backgroundColor: accent }}/>
      <div className="flex items-start justify-between mt-1">
        <span className="text-[10px] uppercase tracking-[0.18em] opacity-65">{label}</span>
        <span
          className="h-9 w-9 rounded-md flex items-center justify-center transition-transform group-hover:scale-110 border"
          style={{ backgroundColor: `${accent}1a`, color: accent, borderColor: `${accent}40` }}
        >
          <Icon size={15} strokeWidth={1.5}/>
        </span>
      </div>
      <div className="display text-3xl mt-3 tabular-nums font-bold">{value}</div>
      <div className="flex items-end justify-between mt-2.5 gap-2">
        {delta !== undefined && delta !== null ? (
          <div className={`text-[11px] flex items-center gap-1 tabular-nums font-bold ${positive ? 'text-emerald-500' : 'text-rose-500'}`}>
            {positive ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
            {positive ? '+' : ''}{delta.toFixed(1)}% QoQ
          </div>
        ) : <div className="text-[11px] opacity-50">—</div>}
        {sparkData && <Sparkline data={sparkData} color={accent}/>}
      </div>
    </div>
  );
}

const HEALTH_TONE = {
  green:  { bg: 'rgba(16,185,129,0.15)',  fg: '#10B981', label: 'Healthy' },
  amber:  { bg: 'rgba(245,158,11,0.15)',  fg: '#F59E0B', label: 'Watch'   },
  red:    { bg: 'rgba(239,68,68,0.15)',   fg: '#EF4444', label: 'At risk' },
};

export default function IncubatorDashboard() {
  const { user } = useAuth();
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [s] = await Promise.all([
          api.get('/startups/').catch(() => ({ data: [] })),
        ]);
        if (cancel) return;
        queueMicrotask(() => {
          setStartups(s.data || []);
          setLoading(false);
        });
      } catch {
        if (cancel) return;
        queueMicrotask(() => setLoading(false));
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Incubator-friendly demo enrichment. Some incubator tenants have
  // real `startups` but no mentor / cohort / health metadata yet — we
  // backfill those slots with sensible demo defaults so the cockpit
  // never looks half-built. The numeric KPIs prefer real data.
  const data = useMemo(() => {
    const isReal = startups.length > 0;
    const totalRaised = startups.reduce((s, x) => s + (x.funding_raised || 0), 0);
    const avg = (k) => {
      const vs = startups.map((s) => s[k]).filter((v) => typeof v === 'number');
      if (!vs.length) return 0;
      return Math.round(vs.reduce((a, b) => a + b, 0) / vs.length);
    };
    const realHealth = avg('health_score');
    const demoFill = {
      cohortName: isReal ? 'Active Cohort' : 'Cohort Alif · Q1 2026',
      cohortWeek: 7,
      cohortTotal: 12,
      activeStartups: isReal ? startups.length : 24,
      activeDelta: 12.5,
      totalRaised: isReal && totalRaised > 0 ? totalRaised : 18_400_000,
      totalRaisedDelta: 28.7,
      avgHealth: isReal && realHealth > 0 ? realHealth : 76,
      avgHealthDelta: 4.2,
      activeMentors: 18,
      activeMentorsDelta: 11.1,
      sparkStartups: [16, 18, 19, 21, 22, 23, isReal ? startups.length : 24],
      sparkRaised: [9.5, 11.2, 12.8, 14.5, 16.0, 17.2, isReal && totalRaised > 0 ? totalRaised / 1e6 : 18.4],
      sparkHealth: [70, 71, 72, 73, 74, 75, isReal && realHealth > 0 ? realHealth : 76],
      sparkMentors: [14, 14, 15, 16, 17, 17, 18],
      healthMix: { green: 16, amber: 6, red: 2 },
      mentorHeatmap: [
        { name: 'K. Al-Thani',   load: 95 },
        { name: 'L. Mahmoud',    load: 78 },
        { name: 'A. Bassam',     load: 62 },
        { name: 'M. Otaibi',     load: 45 },
        { name: 'F. Salem',      load: 38 },
        { name: 'H. Nasser',     load: 22 },
      ],
      portfolio: isReal
        ? startups
            .slice()
            .sort((a, b) => (b.health_score || 0) - (a.health_score || 0))
            .slice(0, 5)
            .map((s) => {
              const h = s.health_score || 60;
              const tone = h >= 80 ? 'green' : h >= 60 ? 'amber' : 'red';
              return {
                id: s.startup_id || s.id,
                name: s.name || 'Untitled',
                health: h,
                sector: s.sector || '—',
                raised: s.funding_raised || 0,
                tone,
              };
            })
        : [
            { id: 'p1', name: 'Sahel Robotics',  health: 94, sector: 'Robotics',   raised: 8_500_000, tone: 'green' },
            { id: 'p2', name: 'Falaj Energy',    health: 88, sector: 'CleanTech',  raised: 25_000_000, tone: 'green' },
            { id: 'p3', name: 'Iqra Health AI',  health: 72, sector: 'HealthTech', raised: 4_200_000, tone: 'amber' },
            { id: 'p4', name: 'Pearl Lending',   health: 80, sector: 'FinTech',    raised: 12_000_000, tone: 'green' },
            { id: 'p5', name: 'Mirage Logistics', health: 58, sector: 'Supply',     raised: 1_400_000, tone: 'red' },
          ],
      isDemo: !isReal,
    };
    return demoFill;
  }, [startups]);

  const healthTotal = Math.max(1, data.healthMix.green + data.healthMix.amber + data.healthMix.red);
  const cohortProgress = Math.round((data.cohortWeek / data.cohortTotal) * 100);

  return (
    <div className="space-y-7" data-testid="incubator-cockpit">
      {/* ── 0. Hero greeting ── */}
      <div className="ve-card p-6 flex items-start gap-5 flex-wrap">
        <div className="h-14 w-14 rounded-md flex items-center justify-center shrink-0"
             style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
          <GraduationCap size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="ve-eyebrow" style={{ color: '#10B981' }}>Incubator Cockpit · {data.cohortName}</div>
          <h1 className="display text-2xl mt-1" data-testid="incubator-greeting">
            Welcome back, {user?.first_name || user?.email?.split('@')[0] || 'Director'}.
          </h1>
          <p className="text-sm opacity-70 mt-1 max-w-3xl">
            Week {data.cohortWeek} of {data.cohortTotal} · {data.activeStartups} active startups · {data.activeMentors} mentors engaged.
          </p>
        </div>
        <div className="flex flex-col gap-2 items-stretch">
          <Link to="/dashboard/founders" className="ve-btn-primary text-[11px] inline-flex items-center gap-1.5 justify-center">
            Open cohort <ChevronRight size={12}/>
          </Link>
          <Link to="/dashboard/founder-vetting" className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ve-violet-bright)] hover:underline text-center">
            Start new vetting →
          </Link>
        </div>
      </div>

      {/* ── 1. KPI strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile testid="kpi-active-startups" label="Active Startups"   value={data.activeStartups}            delta={data.activeDelta}       sparkData={data.sparkStartups} icon={Rocket}    accent="#10B981"/>
        <KpiTile testid="kpi-total-raised"     label="Cohort Raised"     value={fmtMoney(data.totalRaised)}     delta={data.totalRaisedDelta}  sparkData={data.sparkRaised}   icon={TrendingUp} accent="#8B3FC5"/>
        <KpiTile testid="kpi-avg-health"       label="Avg Health Score"  value={`${data.avgHealth}/100`}        delta={data.avgHealthDelta}    sparkData={data.sparkHealth}   icon={Activity}  accent="#0EA5E9"/>
        <KpiTile testid="kpi-active-mentors"   label="Active Mentors"    value={data.activeMentors}             delta={data.activeMentorsDelta} sparkData={data.sparkMentors} icon={Users}     accent="#F59E0B"/>
      </div>

      {/* ── 2. Cohort progress + AI Insights ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-4">
        <div className="ve-card p-5" data-testid="incubator-cohort">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="ve-eyebrow" style={{ color: '#10B981' }}>Cohort Progress</div>
              <h3 className="display text-base mt-1">Week {data.cohortWeek} of {data.cohortTotal}</h3>
            </div>
            <Award size={18} className="opacity-50"/>
          </div>
          <div className="h-3 rounded-full overflow-hidden bg-[rgba(255,255,255,0.05)]">
            <div className="h-full rounded-full transition-all" style={{ width: `${cohortProgress}%`, background: 'linear-gradient(90deg, #10B981 0%, #34D399 100%)' }}/>
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px]">
            <span className="font-bold text-emerald-500 pal-mono">{cohortProgress}%</span>
            <span className="opacity-65">{data.cohortTotal - data.cohortWeek} weeks remaining · demo day in 5 weeks</span>
          </div>
          {/* Health distribution */}
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-65 mb-2">Startup health distribution</div>
            <div className="h-3 rounded-full overflow-hidden flex">
              {['green', 'amber', 'red'].map((k) => {
                const n = data.healthMix[k];
                const pct = (n / healthTotal) * 100;
                return <div key={k} style={{ width: `${pct}%`, background: HEALTH_TONE[k].fg }} className="h-full" title={`${HEALTH_TONE[k].label}: ${n}`}/>;
              })}
            </div>
            <div className="mt-2 flex flex-wrap gap-3">
              {['green', 'amber', 'red'].map((k) => (
                <div key={k} className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: HEALTH_TONE[k].fg }}/>
                  <span className="text-[11px] opacity-80">{HEALTH_TONE[k].label}</span>
                  <span className="text-[11px] pal-mono font-bold">{data.healthMix[k]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ve-card p-5" data-testid="incubator-iqra-insights">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="ve-eyebrow">Iqra AI · Insights</div>
              <h3 className="display text-base mt-1">3 things to focus on</h3>
            </div>
            <Sparkles size={18} className="text-[var(--ve-violet-bright)]"/>
          </div>
          <div className="space-y-3 text-[13px]">
            <div className="p-3 rounded border-l-2 border-rose-500 bg-rose-500/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-rose-500 mb-1 flex items-center gap-1.5"><AlertTriangle size={11}/> At risk</div>
              <span className="opacity-90">Mirage Logistics health dropped 18 points in 30 days. Mentor escalation recommended.</span>
            </div>
            <div className="p-3 rounded border-l-2 border-amber-500 bg-amber-500/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-500 mb-1 flex items-center gap-1.5"><Users size={11}/> Mentor cap</div>
              <span className="opacity-90">K. Al-Thani at 95% load — rebalance 2 startups before week 8.</span>
            </div>
            <div className="p-3 rounded border-l-2 border-emerald-500 bg-emerald-500/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-500 mb-1 flex items-center gap-1.5"><Flame size={11}/> Intro signal</div>
              <span className="opacity-90">Falaj Energy + Sahel Robotics share 4 graph edges — schedule a joint Shura.</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3. Top portfolio + Mentor heatmap ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
        <div className="ve-card p-5" data-testid="incubator-portfolio">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="ve-eyebrow">Top portfolio</div>
              <h3 className="display text-base mt-1">By health score</h3>
            </div>
            <Target size={18} className="opacity-50"/>
          </div>
          <div className="space-y-2">
            {data.portfolio.map((p, i) => {
              const t = HEALTH_TONE[p.tone];
              return (
                <Link
                  key={p.id}
                  to={`/dashboard/startups/${p.id}`}
                  data-testid={`portfolio-item-${i}`}
                  className="flex items-center gap-4 p-3 rounded border border-[rgba(174,82,234,0.10)] hover:border-[var(--ve-violet)] hover:bg-[var(--ve-violet)]/5 transition-colors group"
                >
                  <span className="h-8 w-8 rounded-md flex items-center justify-center shrink-0" style={{ background: t.bg, color: t.fg }}>
                    <Rocket size={14}/>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] opacity-55 mt-0.5">{p.sector} · {fmtMoney(p.raised)} raised</div>
                  </div>
                  <div className="text-right">
                    <div className="pal-mono font-bold tabular-nums" style={{ color: t.fg }}>{p.health}/100</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] opacity-55 mt-0.5">{t.label}</div>
                  </div>
                  <ChevronRight size={14} className="opacity-40 group-hover:opacity-80 group-hover:translate-x-1 transition-all shrink-0"/>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="ve-card p-5" data-testid="incubator-mentor-heatmap">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="ve-eyebrow">Mentor utilization</div>
              <h3 className="display text-base mt-1">Capacity heatmap</h3>
            </div>
            <Heart size={18} className="opacity-50"/>
          </div>
          <div className="space-y-2.5">
            {data.mentorHeatmap.map((m) => {
              const color = m.load >= 85 ? '#EF4444' : m.load >= 65 ? '#F59E0B' : '#10B981';
              return (
                <div key={m.name} className="flex items-center gap-3">
                  <span className="text-[11px] font-bold opacity-80 w-28 shrink-0 truncate">{m.name}</span>
                  <div className="flex-1 h-5 rounded overflow-hidden relative" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <div className="h-full transition-all" style={{ width: `${m.load}%`, background: color }}/>
                    <span className="absolute inset-0 flex items-center justify-end pe-2 pal-mono text-[10px] font-bold">{m.load}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── 4. Action items ── */}
      <div className="ve-card p-5" data-testid="incubator-actions">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="ve-eyebrow">Action items</div>
            <h3 className="display text-base mt-1">5 tasks this week</h3>
          </div>
          <Clock size={18} className="opacity-50"/>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[
            { tone: 'rose',    icon: AlertTriangle, label: 'Escalate Mirage Logistics — health -18', due: 'today' },
            { tone: 'amber',   icon: Users,         label: 'Rebalance K. Al-Thani mentor load',       due: 'in 2 days' },
            { tone: 'emerald', icon: CheckCircle2,  label: 'Approve 3 new founder vettings',          due: 'today' },
            { tone: 'sky',     icon: Award,         label: 'Schedule demo-day investor list',         due: 'in 5 days' },
            { tone: 'violet',  icon: Flame,         label: 'Run Joint Shura · Falaj + Sahel',         due: 'in 3 days' },
          ].map((a, i) => {
            const I = a.icon;
            return (
              <div key={i} className={`flex items-start gap-2.5 p-2.5 rounded border-l-2 border-${a.tone}-500 bg-${a.tone}-500/[0.04]`}>
                <I size={13} className={`text-${a.tone}-500 mt-0.5 shrink-0`}/>
                <div className="flex-1 min-w-0 text-[13px]">
                  <div className="font-medium opacity-90 truncate">{a.label}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] opacity-50 mt-0.5">{a.due}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="text-center text-[11px] opacity-50">Refreshing cockpit…</div>
      )}
    </div>
  );
}
